import * as React from 'react'
import { Download, FileText, Loader2 } from 'lucide-react'
import type { DocumentDto } from '@lib/dto/document.dto'
import { formatBytes } from '@lib/constants/documents'
import { formatDate } from '@lib/helpers/format'
import { useDownloadDocument } from '@/hooks/useDownloadDocument'
import { useArchiveDocument } from '@/hooks/useDocumentMutations'
import { toErrorMessage } from '@/helpers/logger'
import { DocumentKindBadge } from '@/components/ClientBadges'
import { Button } from '@/components/ui/button'
import {
  COL_OPTIONAL,
  COL_SECONDARY,
  COL_STICKY,
  ROW,
  ROW_ACTION,
  TABLE,
  TABLE_FRAME,
  TD,
  TD_RIGHT,
  TH,
  TH_RIGHT,
} from '@/components/ui/table-classes'
import { cn } from '@/lib/utils'

interface DocumentTableProps {
  documents: DocumentDto[]
  isPending?: boolean
  isError?: boolean
  emptyMessage: string
}

/**
 * A dense table of files, with download and archive on row hover.
 *
 * A document whose upload never finished is shown rather than filtered out, and
 * labeled: hiding it would leave someone wondering where their file went, and
 * the row is the only place they can see that the upload failed.
 */
export function DocumentTable({
  documents,
  isPending = false,
  isError = false,
  emptyMessage,
}: DocumentTableProps) {
  const { download, pendingIds, error: downloadError } = useDownloadDocument()
  const archiveDocument = useArchiveDocument()
  const [actionError, setActionError] = React.useState<string | null>(null)

  async function handleArchiveToggle(item: DocumentDto): Promise<void> {
    setActionError(null)
    try {
      await archiveDocument.mutateAsync({
        id: item.id,
        archived: item.archivedAt === null,
      })
    } catch (caught) {
      setActionError(toErrorMessage(caught))
    }
  }

  if (isPending) {
    return (
      <div className="rounded-lg border border-default bg-surface">
        {[0, 1].map((row) => (
          <div
            key={row}
            className="flex items-center gap-4 border-b border-default px-3 py-2 last:border-b-0"
          >
            {[0, 1, 2].map((column) => (
              <div
                key={column}
                className="h-3.5 flex-1 animate-pulse rounded bg-subtle"
              />
            ))}
          </div>
        ))}
      </div>
    )
  }

  if (isError) {
    return (
      <Empty>Could not load files. Refresh the page to try again.</Empty>
    )
  }

  if (documents.length === 0) {
    return <Empty>{emptyMessage}</Empty>
  }

  return (
    <div className="space-y-2">
      {downloadError ? (
        <p className="text-sm text-destructive">{downloadError}</p>
      ) : null}
      {actionError ? (
        <p className="text-sm text-destructive">{actionError}</p>
      ) : null}
      <div className={TABLE_FRAME}>
        <table className={TABLE}>
          <thead>
            <tr className="border-b border-default text-secondary-text">
              <th className={cn(TH, COL_STICKY, 'bg-surface')}>File</th>
              <th className={TH}>Kind</th>
              <th className={cn(TH_RIGHT, COL_SECONDARY)}>Size</th>
              <th className={cn(TH, COL_SECONDARY)}>Added</th>
              <th className={cn(TH, COL_OPTIONAL)}>By</th>
              <th className={TH} />
            </tr>
          </thead>
          <tbody>
            {documents.map((item) => (
              <tr
                key={item.id}
                className={cn(
                  ROW,
                  'group border-b border-default last:border-b-0',
                  item.archivedAt ? 'opacity-55' : null,
                )}
              >
                <td className={cn(TD, COL_STICKY)}>
                  <span className="inline-flex items-center gap-1.5">
                    <FileText className="h-3.5 w-3.5 shrink-0 text-muted-text" />
                    <span className="font-medium">
                      {item.title ?? item.fileName}
                    </span>
                  </span>
                  {item.title ? (
                    <span className="ml-2 text-xs text-muted-text">
                      {item.fileName}
                    </span>
                  ) : null}
                  {item.status === 'PENDING' ? (
                    <span className="ml-2 text-xs text-warning">
                      Upload never finished
                    </span>
                  ) : null}
                  {item.archivedAt ? (
                    <span className="ml-2 text-xs text-muted-text">
                      Archived
                    </span>
                  ) : null}
                </td>
                <td className={TD}>
                  <DocumentKindBadge kind={item.kind} />
                </td>
                <td className={cn(TD_RIGHT, COL_SECONDARY, 'tabular-nums text-secondary-text')}>
                  {item.status === 'READY' ? formatBytes(item.byteSize) : '—'}
                </td>
                <td className={cn(TD, COL_SECONDARY, 'text-muted-text')}>
                  {formatDate(item.createdAt)}
                </td>
                <td className={cn(TD, COL_OPTIONAL, 'text-muted-text')}>
                  {item.uploadedByName ?? '—'}
                </td>
                <td className={cn(TD, 'text-right')}>
                  <span className={cn('inline-flex gap-1', ROW_ACTION)}>
                    {item.status === 'READY' ? (
                      <Button
                        size="sm"
                        variant="ghost"
                        disabled={pendingIds.has(item.id)}
                        onClick={() => {
                          void download(item.id)
                        }}
                      >
                        {pendingIds.has(item.id) ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                          <Download className="h-3.5 w-3.5" />
                        )}
                        Download
                      </Button>
                    ) : null}
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        void handleArchiveToggle(item)
                      }}
                    >
                      {item.archivedAt ? 'Restore' : 'Archive'}
                    </Button>
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function Empty({ children }: { children: React.ReactNode }) {
  return (
    <div className="rounded-lg border border-default bg-surface px-3 py-8 text-center">
      <FileText className="mx-auto h-5 w-5 text-disabled-text" />
      <p className="mt-2 text-sm text-muted-text">{children}</p>
    </div>
  )
}
